import React, { useEffect, useState } from "react";
import { Check, ScrollText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectBusinessProfile,
  selectBusinessProfileError,
} from "../business-profile-slice";
import {
  fetchBusinessProfile,
  updateBusinessProfile,
} from "../slice-extensions/business-profile-slice-extension";

/**
 * Business details for invoices / receipts: letterhead fields, the reusable
 * footer note (tax statement), and the read-only invoice-number counter.
 */
const BusinessDetailsSection: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const profile = useAppSelector(selectBusinessProfile);
  const lastError = useAppSelector(selectBusinessProfileError);

  const [businessName, setBusinessName] = useState("");
  const [address, setAddress] = useState("");
  const [taxId, setTaxId] = useState("");
  const [iban, setIban] = useState("");
  const [footerNote, setFooterNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    dispatch(fetchBusinessProfile());
  }, [dispatch]);

  useEffect(() => {
    if (profile) {
      setBusinessName(profile.businessName);
      setAddress(profile.address ?? "");
      setTaxId(profile.taxId ?? "");
      setIban(profile.iban ?? "");
      setFooterNote(profile.defaultFooterNote ?? "");
    }
  }, [profile]);

  const isDirty =
    profile !== null &&
    (businessName.trim() !== profile.businessName ||
      address.trim() !== (profile.address ?? "") ||
      taxId.trim() !== (profile.taxId ?? "") ||
      iban.trim() !== (profile.iban ?? "") ||
      footerNote.trim() !== (profile.defaultFooterNote ?? ""));

  const handleSave = async () => {
    if (!isDirty || businessName.trim() === "") return;
    setSaving(true);
    setSaved(false);
    const result = await dispatch(
      updateBusinessProfile({
        businessName: businessName.trim(),
        address: address.trim() || null,
        taxId: taxId.trim() || null,
        iban: iban.trim() || null,
        defaultFooterNote: footerNote.trim() || null,
      })
    );
    setSaving(false);
    if (updateBusinessProfile.fulfilled.match(result)) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  const fieldLabel = "block text-sm font-medium text-grey-30 dark:text-white mb-1";

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
          <ScrollText size={20} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold leading-tight">
            {t("businessProfile.title")}
          </h2>
          <p className="text-sm text-grey-50 dark:text-grey-70 mt-0.5">
            {t("businessProfile.description")}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className={fieldLabel} htmlFor="business_name">
            {t("businessProfile.businessName")}
          </label>
          <input
            id="business_name"
            type="text"
            className="input w-full lg:max-w-md"
            value={businessName}
            maxLength={200}
            onChange={(e) => setBusinessName(e.target.value)}
            disabled={profile === null || saving}
          />
        </div>

        <div>
          <label className={fieldLabel} htmlFor="business_address">
            {t("businessProfile.address")}
          </label>
          <textarea
            id="business_address"
            className="input w-full lg:max-w-md min-h-20"
            value={address}
            maxLength={500}
            rows={3}
            onChange={(e) => setAddress(e.target.value)}
            disabled={profile === null || saving}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 lg:max-w-md lg:grid-cols-2">
          <div>
            <label className={fieldLabel} htmlFor="business_tax_id">
              {t("businessProfile.taxId")}
            </label>
            <input
              id="business_tax_id"
              type="text"
              className="input w-full"
              value={taxId}
              maxLength={50}
              onChange={(e) => setTaxId(e.target.value)}
              disabled={profile === null || saving}
            />
          </div>
          <div>
            <label className={fieldLabel} htmlFor="business_iban">
              {t("businessProfile.iban")}
            </label>
            <input
              id="business_iban"
              type="text"
              className="input w-full"
              value={iban}
              maxLength={50}
              onChange={(e) => setIban(e.target.value)}
              disabled={profile === null || saving}
            />
          </div>
        </div>

        <div>
          <label className={fieldLabel} htmlFor="business_footer_note">
            {t("businessProfile.footerNote")}
          </label>
          <textarea
            id="business_footer_note"
            className="input w-full lg:max-w-md min-h-20"
            value={footerNote}
            maxLength={1000}
            rows={3}
            placeholder={t("businessProfile.footerNotePlaceholder")}
            onChange={(e) => setFooterNote(e.target.value)}
            disabled={profile === null || saving}
          />
          <p className="mt-1 text-xs text-grey-50 dark:text-grey-70">
            {t("businessProfile.footerNoteHint")}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!isDirty || businessName.trim() === "" || saving}
          >
            {saving ? t("common.saving") : t("common.saveChanges")}
          </button>
          {saved && (
            <span className="flex items-center gap-1 text-sm text-green-700 dark:text-green-400">
              <Check size={16} /> {t("businessProfile.saved")}
            </span>
          )}
        </div>

        {profile !== null && (
          <p className="text-xs text-grey-50 dark:text-grey-70">
            {t("businessProfile.nextInvoiceNumber", { number: profile.nextInvoiceNumber })}
          </p>
        )}

        {lastError && (
          <p className="text-sm text-red-600 dark:text-red-400">{lastError.message}</p>
        )}
      </div>
    </div>
  );
};

export default BusinessDetailsSection;
